import './NavDrawer.scss'
import { useCallback, useLayoutEffect, useRef, type ReactNode } from 'react'
import { MdOutlineClose } from 'react-icons/md'
import { IconButton, Portal, useModalLayer, usePresence } from '../../ui'

export interface NavDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** The navigation (a `<Sidebar form="drawer">`). */
  children: ReactNode
}

/**
 * The navigation below `tablet` (ADR 0012): a modal dialog from the start
 * edge. Focus trap, inert background, scroll lock and Escape come from
 * useModalLayer; a press on the scrim closes it. AppShell returns focus to
 * the menu button.
 */
export function NavDrawer({ isOpen, onClose, children }: NavDrawerProps) {
  const layerRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  useLayoutEffect(() => {
    onCloseRef.current = onClose
  })
  const onEscape = useCallback(() => {
    onCloseRef.current()
  }, [])

  useModalLayer(layerRef, { open: isOpen, onEscape })

  // Slides back out before unmounting (ADR 0019).
  const { isMounted, isClosing } = usePresence(isOpen)
  if (!isMounted) return null
  return (
    <Portal>
      <div
        ref={layerRef}
        className="nav-drawer"
        data-state={isClosing ? 'closed' : 'open'}
        inert={isClosing || undefined}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className="nav-drawer__surface"
        >
          <div className="nav-drawer__header">
            <IconButton
              label="Close navigation"
              icon={<MdOutlineClose />}
              hasTooltip={false}
              onClick={onClose}
            />
          </div>
          {children}
        </div>
      </div>
    </Portal>
  )
}
